(() => {
  if (window.__lenchoHeaderModuleReady) return;
  window.__lenchoHeaderModuleReady = true;
  const SELECTOR = '[data-lencho-header]';
  const escapeHtml = (value = '') => String(value).replace(/[&<>"']/g, (char) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#039;'
  })[char]);

  const imageUrl = (image) => {
    if (!image) return '';
    if (typeof image === 'string') return image;
    return image.url || image.src || image.originalSrc || '';
  };

  const formatMoney = (amount) => {
    if (amount === undefined || amount === null || !Number.isFinite(Number(amount))) return '';
    try {
      const currency = window.Shopify?.currency?.active || 'INR';
      return new Intl.NumberFormat(document.documentElement.lang || 'en-IN', {
        style: 'currency', currency, minimumFractionDigits: 0, maximumFractionDigits: 2
      }).format(Number(amount));
    } catch (error) {
      return `₹${Number(amount).toFixed(2)}`;
    }
  };

  function initHeader(root) {
    if (!root || root.dataset.ready === 'true') return;
    root.dataset.ready = 'true';

    const drawer = root.querySelector('[data-lh-mobile-drawer]');
    const search = root.querySelector('[data-lh-search-overlay]');
    const searchInput = root.querySelector('[data-lh-search-input]');
    const predictive = root.querySelector('[data-lh-predictive]');
    const popular = root.querySelector('[data-lh-search-popular]');
    const desktopItems = root.querySelectorAll('[data-lh-desktop-item]');
    const wishlistKey = root.dataset.wishlistKey || 'lencho_wishlist_products';
    const sticky = root.dataset.sticky === 'true';
    const hideOnScroll = root.dataset.hideOnScroll === 'true';
    const predictiveEnabled = root.dataset.predictive === 'true';
    const stickyLayer = root.closest('#header-group');
    if (sticky && stickyLayer) {
      stickyLayer.classList.add('lencho-header-group--sticky');
      stickyLayer.style.setProperty(
        '--lencho-header-group-z',
        getComputedStyle(root).getPropertyValue('--lh-z').trim() || '40'
      );
    }
    let lastFocus = null;
    let previousScroll = window.scrollY;
    let searchTimer;
    let desktopCloseTimer;
    let predictiveController;
    const lifecycle = new AbortController();
    const listen = (target, type, handler, options = {}) => {
      target?.addEventListener(type, handler, { ...options, signal: lifecycle.signal });
    };
    root.__lenchoHeaderCleanup = () => {
      clearTimeout(searchTimer);
      clearTimeout(desktopCloseTimer);
      if (frame) cancelAnimationFrame(frame);
      predictiveController?.abort();
      lifecycle.abort();
      document.body.classList.remove('lencho-header-lock');
      stickyLayer?.classList.remove('lencho-header-group--sticky');
      stickyLayer?.style.removeProperty('--lencho-header-group-z');
    };

    const updateLock = () => {
      const open = drawer?.classList.contains('is-open') || search?.classList.contains('is-open');
      document.body.classList.toggle('lencho-header-lock', Boolean(open));
      root.classList.toggle('is-menu-open', Boolean(drawer?.classList.contains('is-open')));
      root.classList.toggle('is-search-open', Boolean(search?.classList.contains('is-open')));
    };

    const openDrawer = (trigger) => {
      if (!drawer) return;
      lastFocus = trigger || document.activeElement;
      drawer.classList.add('is-open');
      drawer.setAttribute('aria-hidden', 'false');
      updateLock();
      requestAnimationFrame(() => drawer.querySelector('[data-lh-mobile-close]')?.focus());
    };

    const closeDrawer = () => {
      if (!drawer?.classList.contains('is-open')) return;
      drawer.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
      updateLock();
      lastFocus?.focus?.();
    };

    const openSearch = (trigger) => {
      if (!search) return;
      closeDrawer();
      lastFocus = trigger || document.activeElement;
      search.classList.add('is-open');
      search.setAttribute('aria-hidden', 'false');
      updateLock();
      requestAnimationFrame(() => searchInput?.focus());
    };

    const closeSearch = () => {
      if (!search?.classList.contains('is-open')) return;
      search.classList.remove('is-open');
      search.setAttribute('aria-hidden', 'true');
      updateLock();
      lastFocus?.focus?.();
    };

    root.querySelectorAll('[data-lh-mobile-open]').forEach((button) => button.addEventListener('click', () => openDrawer(button)));
    root.querySelectorAll('[data-lh-mobile-close]').forEach((button) => button.addEventListener('click', closeDrawer));
    root.querySelectorAll('[data-lh-search-open]').forEach((button) => button.addEventListener('click', () => openSearch(button)));
    root.querySelectorAll('[data-lh-search-close]').forEach((button) => button.addEventListener('click', closeSearch));

    root.querySelectorAll('[data-lh-mobile-submenu-toggle]').forEach((button) => {
      button.addEventListener('click', () => {
        const panel = document.getElementById(button.getAttribute('aria-controls'));
        if (!panel) return;
        const open = button.getAttribute('aria-expanded') !== 'true';
        button.setAttribute('aria-expanded', String(open));
        panel.hidden = !open;
      });
    });

    const setCollectionPreview = (trigger) => {
      const dropdown = trigger?.closest('[data-lh-dropdown]');
      const previewKey = trigger?.getAttribute('data-lh-collection-preview-trigger');
      if (!dropdown || previewKey === null) return;
      dropdown.querySelectorAll('[data-lh-collection-preview-trigger]').forEach((item) => {
        item.classList.toggle('is-preview-active', item === trigger);
      });
      dropdown.querySelectorAll('[data-lh-collection-preview]').forEach((panel) => {
        const active = panel.getAttribute('data-lh-collection-preview') === previewKey;
        panel.hidden = !active;
        panel.classList.toggle('is-active', active);
      });
    };

    root.querySelectorAll('[data-lh-collection-preview-trigger]').forEach((trigger) => {
      listen(trigger, 'pointerenter', (event) => {
        if (event.pointerType === 'touch' || !window.matchMedia('(hover: hover)').matches) return;
        setCollectionPreview(trigger);
      });
      listen(trigger, 'focus', () => setCollectionPreview(trigger));
    });

    const closeDesktopMenus = (except = null) => {
      desktopItems.forEach((item) => {
        if (item === except) return;
        item.classList.remove('is-open');
        item.querySelector('[data-lh-desktop-link]')?.setAttribute('aria-expanded', 'false');
        item.querySelector('[data-lh-desktop-toggle]')?.setAttribute('aria-expanded', 'false');
      });
    };

    desktopItems.forEach((item) => {
      const toggle = item.querySelector('[data-lh-desktop-toggle]');
      const link = item.querySelector('[data-lh-desktop-link]');
      const setOpen = (open) => {
        item.classList.toggle('is-open', open);
        toggle?.setAttribute('aria-expanded', String(open));
        link?.setAttribute('aria-expanded', String(open));
      };
      const toggleMenu = (event) => {
        event.preventDefault();
        event.stopPropagation();
        const open = !item.classList.contains('is-open');
        closeDesktopMenus(open ? item : null);
        setOpen(open);
      };
      toggle?.addEventListener('click', toggleMenu);
      if (link?.matches('button[data-lh-desktop-link]')) link.addEventListener('click', toggleMenu);
      link?.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowDown') {
          event.preventDefault();
          closeDesktopMenus(item);
          setOpen(true);
          item.querySelector('[data-lh-dropdown] a')?.focus();
        }
      });

      if (item.classList.contains('has-dropdown')) {
        listen(item, 'pointerenter', (event) => {
          if (event.pointerType === 'touch' || !window.matchMedia('(hover: hover)').matches) return;
          clearTimeout(desktopCloseTimer);
          closeDesktopMenus(item);
          setOpen(true);
        });
        listen(item, 'pointerleave', (event) => {
          if (event.pointerType === 'touch' || !window.matchMedia('(hover: hover)').matches) return;
          clearTimeout(desktopCloseTimer);
          desktopCloseTimer = setTimeout(() => {
            if (!item.matches(':hover') && !item.contains(document.activeElement)) setOpen(false);
          }, 180);
        });
        listen(item, 'focusin', () => {
          clearTimeout(desktopCloseTimer);
          closeDesktopMenus(item);
          setOpen(true);
        });
        listen(item, 'focusout', (event) => {
          if (item.contains(event.relatedTarget)) return;
          clearTimeout(desktopCloseTimer);
          desktopCloseTimer = setTimeout(() => setOpen(false), 120);
        });
      }
    });

    listen(document, 'click', (event) => {
      if (!root.contains(event.target)) closeDesktopMenus();
    });

    const focusableSelector = 'a[href],button:not([disabled]),input:not([disabled]),[tabindex]:not([tabindex="-1"])';
    const trapFocus = (event, container) => {
      if (event.key !== 'Tab' || !container) return;
      const items = [...container.querySelectorAll(focusableSelector)].filter((item) => item.offsetParent !== null);
      if (!items.length) return;
      const first = items[0], last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    };

    listen(document, 'keydown', (event) => {
      if (event.key === 'Escape') {
        closeDesktopMenus();
        closeDrawer();
        closeSearch();
      }
      if (drawer?.classList.contains('is-open')) trapFocus(event, drawer.querySelector('[role=dialog]'));
      if (search?.classList.contains('is-open')) trapFocus(event, search.querySelector('[role=dialog]'));
    });

    const updateScroll = () => {
      const top = Math.max(window.scrollY, 0);
      root.classList.toggle('is-scrolled', top > 12);
      if (hideOnScroll && sticky && top > 180 && !root.classList.contains('is-menu-open') && !root.classList.contains('is-search-open')) {
        if (top > previousScroll + 4) root.classList.add('is-hidden');
        if (top < previousScroll - 4) root.classList.remove('is-hidden');
      } else {
        root.classList.remove('is-hidden');
      }
      previousScroll = top;
      const header = root.querySelector('.lencho-header');
      if (header) root.style.setProperty('--lh-current-height', `${header.getBoundingClientRect().height}px`);
    };

    let frame;
    listen(window, 'scroll', () => {
      if (frame) return;
      frame = requestAnimationFrame(() => { updateScroll(); frame = null; });
    }, { passive: true });
    listen(window, 'resize', updateScroll, { passive: true });
    updateScroll();

    const readWishlist = () => {
      try {
        const data = JSON.parse(localStorage.getItem(wishlistKey) || '[]');
        return Array.isArray(data) ? data : [];
      } catch (error) {
        return [];
      }
    };

    const updateWishlist = () => {
      const count = readWishlist().length;
      root.querySelectorAll('[data-lh-wishlist-count]').forEach((bubble) => {
        bubble.textContent = count;
        bubble.hidden = count === 0;
      });
    };

    root.querySelectorAll('[data-lh-wishlist-link]').forEach((link) => {
      link.addEventListener('click', (event) => {
        const wishlistDrawer = document.querySelector('[data-lencho-wishlist-drawer],lencho-wishlist-drawer');
        if (wishlistDrawer || link.getAttribute('href') === '#') {
          event.preventDefault();
          document.dispatchEvent(new CustomEvent('lencho:wishlist-open'));
        }
      });
    });
    listen(document, 'lencho:wishlist-updated', updateWishlist);
    listen(window, 'storage', (event) => { if (event.key === wishlistKey) updateWishlist(); });
    updateWishlist();

    const updateCartCount = (count) => {
      const safeCount = Math.max(0, Number(count) || 0);
      root.querySelectorAll('[data-lh-cart-count]').forEach((bubble) => {
        bubble.textContent = safeCount;
        bubble.hidden = safeCount === 0;
      });
    };

    const refreshCart = async () => {
      try {
        const base = window.Shopify?.routes?.root || '/';
        const response = await fetch(`${base}cart.js`, { headers: { Accept: 'application/json' } });
        if (!response.ok) return;
        const cart = await response.json();
        updateCartCount(cart.item_count);
      } catch (error) {}
    };

    const getCartDrawer = () => document.querySelector('theme-drawer#cart-drawer');
    const setCartExpanded = (expanded) => {
      root.querySelectorAll('[data-lh-cart-link][aria-controls="cart-drawer"]').forEach((link) => {
        link.setAttribute('aria-expanded', String(expanded));
      });
    };

    root.querySelectorAll('[data-lh-cart-link]').forEach((link) => {
      link.addEventListener('click', (event) => {
        const cartDrawer = getCartDrawer();
        if (!cartDrawer) return;
        event.preventDefault();
        closeSearch();
        closeDrawer();

        if (window.LenchoCartBridge?.open) {
          window.LenchoCartBridge.open();
          return;
        }

        const openCartDrawer = () => {
          if (typeof cartDrawer.open === 'function') cartDrawer.open();
        };
        if (customElements.get('theme-drawer')) openCartDrawer();
        else customElements.whenDefined('theme-drawer').then(openCartDrawer);
      });
    });
    listen(document, 'theme-drawer:open', (event) => {
      if (event.target?.id === 'cart-drawer') setCartExpanded(true);
    });
    listen(document, 'theme-drawer:close', (event) => {
      if (event.target?.id === 'cart-drawer') setCartExpanded(false);
    });
    listen(document, 'click', (event) => {
      const closeButton = event.target.closest?.('#cart-drawer .theme-drawer__close-button');
      if (!closeButton) return;
      const drawer = getCartDrawer();
      if (typeof drawer?.close === 'function') {
        event.preventDefault();
        drawer.close();
      }
    });

    listen(document, 'shopify:cart:lines-update', (event) => {
      const promise = event.promise;
      if (!promise) {
        refreshCart();
        return;
      }
      promise
        .then(({ cart, detail }) => {
          const itemCount = cart?.totalQuantity ?? detail?.itemCount;
          if (Number.isFinite(Number(itemCount))) updateCartCount(itemCount);
          else refreshCart();
        })
        .catch(() => {});
    });
    listen(document, 'lencho:cart-updated', refreshCart);
    listen(document, 'cart:updated', refreshCart);
    listen(window, 'pageshow', (event) => {
      if (event.persisted) refreshCart();
    });

    const getGroups = (payload) => {
      const results = payload?.resources?.results || payload?.results || {};
      return {
        Products: Array.isArray(results.products) ? results.products : [],
        Collections: Array.isArray(results.collections) ? results.collections : [],
        Pages: Array.isArray(results.pages) ? results.pages : [],
        Articles: Array.isArray(results.articles) ? results.articles : [],
        Suggestions: Array.isArray(results.queries) ? results.queries : []
      };
    };

    const renderResults = (query, payload) => {
      if (!predictive) return;
      const limit = Number(root.dataset.predictiveLimit || 4);
      const groups = getGroups(payload);
      const groupHtml = Object.entries(groups).filter(([, items]) => items.length).map(([label, items]) => {
        const cards = items.slice(0, limit).map((item) => {
          const title = escapeHtml(item.title || item.text || item.query || '');
          const fallbackUrl = `${window.Shopify?.routes?.root || '/'}search?q=${encodeURIComponent(item.text || item.query || query)}`;
          const url = escapeHtml(item.url || fallbackUrl);
          const image = imageUrl(item.featured_image || item.image);
          const media = label === 'Products' ? (image ? `<img src="${escapeHtml(image)}" alt="" loading="lazy">` : '<span class="lencho-header-search__result-placeholder"></span>') : '';
          let meta = label;
          if (label === 'Products' && item.price !== undefined) meta = formatMoney(item.price);
          return `<a class="lencho-header-search__result" href="${url}">${media}<span class="lencho-header-search__result-copy"><strong>${title}</strong><small>${escapeHtml(meta)}</small></span></a>`;
        }).join('');
        return `<section class="lencho-header-search__result-group"><p class="lencho-header-search__predictive-heading">${label}</p><div class="lencho-header-search__result-grid">${cards}</div></section>`;
      }).join('');

      if (!groupHtml) {
        predictive.innerHTML = `<p class="lencho-header-search__status">${escapeHtml(root.dataset.noResultsText)}</p>`;
        return;
      }
      const allUrl = `${window.Shopify?.routes?.root || '/'}search?q=${encodeURIComponent(query)}`;
      predictive.innerHTML = `<div class="lencho-header-search__results">${groupHtml}<a class="lencho-header-search__view-all" href="${allUrl}">${escapeHtml(root.dataset.viewAllText)}</a></div>`;
    };

    const fetchPredictive = async (query) => {
      if (!predictiveEnabled || !predictive) return;
      predictiveController?.abort();
      predictiveController = new AbortController();
      predictive.innerHTML = `<p class="lencho-header-search__status">${escapeHtml(root.dataset.loadingText)}</p>`;
      try {
        const base = window.Shopify?.routes?.root || '/';
        const url = new URL(`${location.origin}${base}search/suggest.json`);
        url.searchParams.set('q', query);
        url.searchParams.set('resources[type]', root.dataset.predictiveTypes || 'product,collection,page');
        url.searchParams.set('resources[limit]', root.dataset.predictiveLimit || '4');
        url.searchParams.set('resources[options][unavailable_products]', root.dataset.unavailableProducts || 'last');
        const response = await fetch(url, { headers: { Accept: 'application/json' }, signal: predictiveController.signal });
        if (!response.ok) throw new Error('Search failed');
        renderResults(query, await response.json());
      } catch (error) {
        if (error.name !== 'AbortError') predictive.innerHTML = `<p class="lencho-header-search__status">${escapeHtml(root.dataset.searchErrorText)}</p>`;
      }
    };

    searchInput?.addEventListener('input', () => {
      clearTimeout(searchTimer);
      const query = searchInput.value.trim();
      if (!query) {
        predictiveController?.abort();
        if (predictive) predictive.innerHTML = '';
        if (popular) popular.hidden = false;
        return;
      }
      if (popular) popular.hidden = true;
      if (query.length < Number(root.dataset.predictiveMinChars || 2)) {
        if (predictive) predictive.innerHTML = `<p class="lencho-header-search__status">${escapeHtml(root.dataset.typeMoreText)}</p>`;
        return;
      }
      searchTimer = setTimeout(() => fetchPredictive(query), Number(root.dataset.predictiveDelay || 200));
    });
  }

  const initAll = (scope = document) => scope.querySelectorAll(SELECTOR).forEach(initHeader);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initAll());
  else initAll();
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    if (event.target?.matches?.(SELECTOR)) event.target.__lenchoHeaderCleanup?.();
    event.target?.querySelectorAll(SELECTOR).forEach((root) => root.__lenchoHeaderCleanup?.());
  });
})();
